import { ref } from 'vue';
import { LocalStorage } from 'shared/helpers/localStorage';

// MakerHub: WhatsApp-style "pin chat" for quick access. Pins are stored per
// browser (localStorage), scoped per account, and kept in shared reactive state
// so the context menu, the row indicator, and the list sort all stay in sync.
// Personal (per-device) by design — not synced across agents/devices.
//
// We keep BOTH the pinned ids AND a trimmed snapshot of each pinned conversation,
// so a pinned chat can always be rendered at the top of the list even when it
// isn't in the currently-loaded page or is filtered out of a tab (e.g. WhatsApp
// groups are hidden from the "All" tab). The live conversation is used when it's
// loaded; the snapshot is the fallback.
const STORAGE_KEY = 'pinned_conversations';
const SNAPSHOT_KEY = 'pinned_conversation_snapshots';
const pinnedIds = ref(new Set());
const pinnedSnapshots = ref({});
let loadedAccountId = null;

const idsKey = accountId => `${STORAGE_KEY}_${accountId}`;
const snapKey = accountId => `${SNAPSHOT_KEY}_${accountId}`;

// Keep only what ConversationCard needs to render a row.
const snapshot = chat => ({
  id: chat.id,
  display_id: chat.display_id,
  inbox_id: chat.inbox_id,
  status: chat.status || 'open',
  unread_count: chat.unread_count || 0,
  priority: chat.priority || null,
  labels: chat.labels || [],
  timestamp: chat.timestamp || chat.last_activity_at || chat.created_at,
  created_at: chat.created_at,
  additional_attributes: chat.additional_attributes || {},
  custom_attributes: chat.custom_attributes || {},
  meta: chat.meta || {},
  messages: (chat.messages || []).slice(-1),
});

const load = accountId => {
  const ids = LocalStorage.get(idsKey(accountId));
  pinnedIds.value = new Set((Array.isArray(ids) ? ids : []).map(Number));
  pinnedSnapshots.value = LocalStorage.get(snapKey(accountId)) || {};
  loadedAccountId = accountId;
};

const persist = accountId => {
  LocalStorage.set(idsKey(accountId), [...pinnedIds.value]);
  LocalStorage.set(snapKey(accountId), pinnedSnapshots.value);
};

export function usePinnedConversations(accountId) {
  if (accountId != null && accountId !== loadedAccountId) load(accountId);

  const isPinned = id => pinnedIds.value.has(Number(id));

  // chat is optional; pass it when pinning so we can snapshot it.
  const togglePin = (id, chat = null) => {
    const numericId = Number(id);
    const nextIds = new Set(pinnedIds.value);
    const nextSnaps = { ...pinnedSnapshots.value };
    if (nextIds.has(numericId)) {
      nextIds.delete(numericId);
      delete nextSnaps[numericId];
    } else {
      nextIds.add(numericId);
      if (chat) nextSnaps[numericId] = snapshot(chat);
    }
    pinnedIds.value = nextIds; // new refs → reactive update
    pinnedSnapshots.value = nextSnaps;
    persist(accountId);
  };

  return { pinnedIds, pinnedSnapshots, isPinned, togglePin };
}
